import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { getTableContainerWidth } from '@atlaskit/editor-common/node-width';
import { tableCellMinWidth, tableNewColumnMinWidth } from '@atlaskit/editor-common/styles';
import { calcTableColumnWidths } from '@atlaskit/editor-common/utils';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { getSelectedTableInfo } from '../../../utils';
import { getColWidthFix, hasTableBeenResized, insertColgroupFromNode } from './colgroup';
import { getCellsRefsInColumn, getColumnStateFromDOM } from './column-state';
import { syncStickyRowToTable } from './dom';
import { getTableMaxWidth, getTableScalingPercent } from './misc';
export var getResizeState = function getResizeState(_ref) {
  var minWidth = _ref.minWidth,
    maxSize = _ref.maxSize,
    table = _ref.table,
    tableRef = _ref.tableRef,
    start = _ref.start,
    domAtPos = _ref.domAtPos,
    _ref$isTableScalingEn = _ref.isTableScalingEnabled,
    isTableScalingEnabled = _ref$isTableScalingEn === void 0 ? false : _ref$isTableScalingEn;
  if (isTableScalingEnabled) {
    var scalePercent = getTableScalingPercent(table, tableRef);
    minWidth = Math.ceil(minWidth / scalePercent);
  }
  // If the table has been resized, we can use the column widths from the table node
  if (hasTableBeenResized(table)) {
    var _cols = calcTableColumnWidths(table).map(function (width, index) {
      return {
        width: width === 0 ? tableNewColumnMinWidth : width,
        minWidth: width === 0 ? tableNewColumnMinWidth : minWidth,
        index: index
      };
    });
    var _widths = _cols.map(function (col) {
      return col.width;
    });
    var _tableWidth = _widths.reduce(function (sum, width) {
      return sum + width;
    }, 0);
    var _overflow = _tableWidth > maxSize;
    return {
      cols: _cols,
      widths: _widths,
      maxSize: maxSize,
      tableWidth: _tableWidth,
      overflow: _overflow,
      isScaled: isTableScalingEnabled
    };
  }
  var shouldReinsertColgroup = !isTableScalingEnabled;

  // Getting the resize state from DOM
  var colgroupChildren = insertColgroupFromNode(tableRef, table, isTableScalingEnabled, shouldReinsertColgroup // don't reinsert colgroup when preserving table width - this causes widths to jump
  );
  var cols = Array.from(colgroupChildren).map(function (_, index) {
    // If the table hasn't been resized and we have a table width attribute, we can use it
    // to calculate the widths of the columns
    if (isTableScalingEnabled) {
      var tableNodeWidth = getTableContainerWidth(table);
      return {
        index: index,
        width: tableNodeWidth / colgroupChildren.length,
        minWidth: minWidth
      };
    }
    var cellsRefs = getCellsRefsInColumn(index, table, start, domAtPos);
    return getColumnStateFromDOM(cellsRefs, index, minWidth);
  });
  var widths = cols.map(function (col) {
    return col.width;
  });
  var tableWidth = widths.reduce(function (sum, width) {
    return sum + width;
  }, 0);
  var overflow = tableWidth > maxSize;
  return {
    cols: cols,
    widths: widths,
    maxSize: maxSize,
    tableWidth: tableWidth,
    overflow: overflow,
    isScaled: isTableScalingEnabled
  };
};

// updates Colgroup DOM node with new widths
export var updateColgroup = function updateColgroup(state, tableRef, tableNode, isTableScalingEnabled) {
  var cols = tableRef.querySelectorAll('col');
  if (getBooleanFF('platform.editor.custom-table-width')) {
    var columnsCount = cols.length;
    if (isTableScalingEnabled && tableNode) {
      var scalePercent = getTableScalingPercent(tableNode, tableRef);
      state.cols.filter(function (column) {
        return column && !!column.width;
      }) // if width is 0, we dont want to apply that.
      .forEach(function (column, i) {
        var fixedColWidth = getColWidthFix(column.width, columnsCount);
        var scaledWidth = fixedColWidth * scalePercent;
        var finalWidth = Math.max(scaledWidth, tableCellMinWidth);
        // we aren't handling the remaining pixels here when the 48px min width is reached
        if (cols[i]) {
          cols[i].style.width = "".concat(finalWidth, "px");
        }
      });
    } else {
      state.cols.filter(function (column) {
        return column && !!column.width;
      }) // if width is 0, we dont want to apply that.
      .forEach(function (column, i) {
        if (cols[i]) {
          cols[i].style.width = "".concat(getColWidthFix(column.width, columnsCount), "px");
        }
      });
    }
  } else {
    state.cols.filter(function (column) {
      return column && !!column.width;
    }) // if width is 0, we dont want to apply that.
    .forEach(function (column, i) {
      if (cols[i]) {
        cols[i].style.width = "".concat(column.width, "px");
      }
    });
  }

  // colgroup has updated, reflect new widths in sticky header
  syncStickyRowToTable(tableRef);
};
export var getTotalWidth = function getTotalWidth(_ref2) {
  var cols = _ref2.cols;
  return cols.reduce(function (totalWidth, col) {
    return totalWidth + col.width;
  }, 0);
};

// adjust columns to take up the total width
// difference in total columns widths vs table width happens due to the "Math.floor"
export var adjustColumnsWidths = function adjustColumnsWidths(resizeState, maxSize) {
  var totalWidth = getTotalWidth(resizeState);
  var diff = maxSize - totalWidth;
  if (diff > 0 || diff < 0 && Math.abs(diff) < tableCellMinWidth) {
    var updated = false;
    return _objectSpread(_objectSpread({}, resizeState), {}, {
      cols: resizeState.cols.map(function (col) {
        if (!updated && col.width + diff > col.minWidth) {
          updated = true;
          return _objectSpread(_objectSpread({}, col), {}, {
            width: col.width + diff
          });
        }
        return col;
      })
    });
  }
  return resizeState;
};
export var evenAllColumnsWidths = function evenAllColumnsWidths(resizeState) {
  var maxSize = getTotalWidth(resizeState);
  var evenWidth = Math.floor(maxSize / resizeState.cols.length);
  var cols = resizeState.cols.map(function (col) {
    return _objectSpread(_objectSpread({}, col), {}, {
      width: evenWidth
    });
  });
  return adjustColumnsWidths(_objectSpread(_objectSpread({}, resizeState), {}, {
    cols: cols
  }), maxSize);
};
var getSpace = function getSpace(columns, start, end) {
  return columns.slice(start, end).map(function (col) {
    return col.width;
  }).reduce(function (sum, width) {
    return sum + width;
  }, 0);
};
export var evenSelectedColumnsWidths = function evenSelectedColumnsWidths(resizeState, rect) {
  var cols = resizeState.cols;
  var selectedSpace = getSpace(cols, rect.left, rect.right);
  var allSpace = getSpace(cols, 0, cols.length);
  var allWidth = allSpace / cols.length;
  var width = selectedSpace / (rect.right - rect.left);

  // Result equals even distribution of all columns -
  // unset widths of all columns
  if (allWidth === width) {
    return _objectSpread(_objectSpread({}, resizeState), {}, {
      widths: cols.map(function () {
        return width;
      }),
      cols: resizeState.cols.map(function (col) {
        return _objectSpread(_objectSpread({}, col), {}, {
          width: 0
        });
      })
    });
  }
  return _objectSpread(_objectSpread({}, resizeState), {}, {
    widths: cols.map(function (col, i) {
      return i >= rect.left && i < rect.right ? width : col.width;
    }),
    cols: cols.map(function (col, i) {
      return _objectSpread(_objectSpread({}, col), {}, {
        width: i >= rect.left && i < rect.right ? width : col.width
      });
    })
  });
};
export var bulkColumnsResize = function bulkColumnsResize(resizeState, columnsIndexes, sourceColumnIndex) {
  var currentTableWidth = getTotalWidth(resizeState);
  var colIndex = columnsIndexes.indexOf(sourceColumnIndex) > -1 ? sourceColumnIndex : sourceColumnIndex + 1;
  var sourceCol = resizeState.cols[colIndex];
  var seenColumns = {};
  var widthsDiffs = [];
  var cols = resizeState.cols.map(function (col) {
    if (columnsIndexes.indexOf(col.index) > -1) {
      var diff = col.width - sourceCol.width;
      if (diff !== 0) {
        widthsDiffs.push(diff);
      }
      return _objectSpread(_objectSpread({}, col), {}, {
        width: sourceCol.width
      });
    }
    return col;
  });
  var newState = _objectSpread(_objectSpread({}, resizeState), {}, {
    cols: cols.map(function (col) {
      if (columnsIndexes.indexOf(col.index) > -1 ||
      // take from prev columns only if dragging the first handle to the left
      columnsIndexes.indexOf(sourceColumnIndex) > -1 && col.index < colIndex) {
        return col;
      }
      while (widthsDiffs.length) {
        var diff = widthsDiffs.shift() || 0;
        var column = seenColumns[col.index] || col;
        var maybeWidth = column.width + diff;
        if (maybeWidth > column.minWidth) {
          seenColumns[column.index] = _objectSpread(_objectSpread({}, column), {}, {
            width: maybeWidth
          });
        } else {
          widthsDiffs.push(maybeWidth - column.minWidth);
          seenColumns[column.index] = _objectSpread(_objectSpread({}, column), {}, {
            width: column.minWidth
          });
          break;
        }
      }
      return seenColumns[col.index] || col;
    })
  });

  // minimum possible table widths at the current layout
  var minTableWidth = resizeState.maxSize;
  // new table widths after bulk resize
  var newTotalWidth = getTotalWidth(newState);
  // when all columns are selected, what do we do when sum of columns widths is lower than min possible table widths?
  if (columnsIndexes.length === resizeState.cols.length && newTotalWidth < minTableWidth) {
    // table is not in overflow -> normal resize of a single column
    if (currentTableWidth === minTableWidth) {
      return resizeState;
    }
    // table is in overflow: keep the dragged column at its widths and evenly distribute columns
    // to recover from overflow state
    else {
      var columnWidth = Math.floor((minTableWidth - sourceCol.width) / (newState.cols.length - 1));
      newState = _objectSpread(_objectSpread({}, resizeState), {}, {
        cols: newState.cols.map(function (col) {
          if (col.index === sourceCol.index) {
            return col;
          }
          return _objectSpread(_objectSpread({}, col), {}, {
            width: columnWidth
          });
        })
      });
    }
  }

  // fix total table widths by adding missing pixels to columns widths here and there
  return adjustColumnsWidths(newState, resizeState.maxSize);
};
export var areColumnsEven = function areColumnsEven(resizeState) {
  var newResizeState = evenAllColumnsWidths(resizeState);
  return newResizeState.cols.every(function (col, i) {
    return col.width === resizeState.cols[i].width;
  });
};

// Get the layout
export var normaliseTableLayout = function normaliseTableLayout(input) {
  switch (input) {
    case 'wide':
      return input;
    case 'full-width':
      return input;
    default:
      return 'default';
  }
};
export var getNewResizeStateFromSelectedColumns = function getNewResizeStateFromSelectedColumns(rect, state, domAtPos, getEditorContainerWidth) {
  var isTableScalingEnabled = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  // Fail early so that we don't do complex calculations for no reason
  var numColumnsSelected = rect.right - rect.left;
  if (numColumnsSelected <= 1) {
    return;
  }
  var _getSelectedTableInfo = getSelectedTableInfo(state.selection),
    totalRowCount = _getSelectedTableInfo.totalRowCount,
    totalColumnCount = _getSelectedTableInfo.totalColumnCount,
    table = _getSelectedTableInfo.table;
  if (!table) {
    return;
  }

  // Fail early so that we don't do complex calculations for no reason
  if (!hasTableBeenResized(table.node)) {
    return;
  }
  var maybeTable = domAtPos(table.start).node;
  var tableRef = maybeTable.closest('table');
  if (!tableRef) {
    return;
  }
  var layout = normaliseTableLayout(tableRef === null || tableRef === void 0 ? void 0 : tableRef.dataset.layout);
  var maxSize = getTableMaxWidth({
    table: table.node,
    tableStart: table.start,
    state: state,
    layout: layout,
    getEditorContainerWidth: getEditorContainerWidth
  });
  var resizeState = getResizeState({
    minWidth: tableCellMinWidth,
    maxSize: maxSize,
    table: table.node,
    tableRef: tableRef,
    start: table.start,
    domAtPos: domAtPos,
    isTableScalingEnabled: isTableScalingEnabled
  });
  var newResizeState = evenSelectedColumnsWidths(resizeState, rect);
  var widthsBefore = resizeState.widths;
  var widthsAfter = newResizeState.widths;
  var changed = resizeState.widths.some(function (widthBefore, index) {
    return widthBefore !== widthsAfter[index];
  });
  return {
    resizeState: newResizeState,
    table: table,
    changed: changed,
    attributes: {
      position: rect.left,
      count: rect.right - rect.left,
      totalRowCount: totalRowCount,
      totalColumnCount: totalColumnCount,
      widthsBefore: widthsBefore,
      widthsAfter: widthsAfter
    }
  };
};